import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useJournal } from "./JournalContext";
import { dateFromKey, formatShortDate } from "./dateUtils";

const snippet = (text, query, maxLen = 110) => {
  const raw = String(text || "").replace(/\s+/g, " ").trim();
  if (!raw) return "";
  const q = query.trim().toLowerCase();
  const lower = raw.toLowerCase();
  const idx = q ? lower.indexOf(q) : 0;
  if (idx < 0) {
    return raw.length <= maxLen ? raw : `${raw.slice(0, maxLen)}…`;
  }
  const start = Math.max(0, idx - 24);
  const slice = raw.slice(start, start + maxLen);
  return `${start > 0 ? "…" : ""}${slice}${start + maxLen < raw.length ? "…" : ""}`;
};

export function searchJournal(entries, query, locale) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const results = [];
  for (const [dateKey, data] of Object.entries(entries || {})) {
    const date = dateFromKey(dateKey);
    const dateStr = formatShortDate(date, locale).toLowerCase();
    const longStr = date.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).toLowerCase();
    const summary = (data?.summary || "").trim();
    const entry = (data?.entry || "").trim();
    const hasSketch = !!data?.drawing;
    const locationText = data?.location
      ? [data.location.name, data.location.city, data.location.country].filter(Boolean).join(", ")
      : "";
    const dateMatch = dateStr.includes(q) || longStr.includes(q) || dateKey.includes(q);
    const summaryMatch = summary.toLowerCase().includes(q);
    const entryMatch = entry.toLowerCase().includes(q);
    const locationMatch = !!locationText && locationText.toLowerCase().includes(q);
    const sketchMatch = hasSketch && (dateMatch || q === "sketch" || q === "drawing");
    if (!dateMatch && !summaryMatch && !entryMatch && !sketchMatch && !locationMatch) continue;

    let type = "entry";
    let preview = "";
    if (entryMatch) {
      type = "entry";
      preview = snippet(entry, q);
    } else if (summaryMatch) {
      type = "moment";
      preview = snippet(summary, q);
    } else if (locationMatch) {
      type = entry ? "entry" : summary ? "moment" : hasSketch ? "sketch" : "entry";
      preview = `📍 ${locationText}${entry || summary ? ` — ${(entry || summary).slice(0, 80)}` : ""}`;
    } else if (sketchMatch && !summary && !entry) {
      type = "sketch";
      preview = "";
    } else {
      type = entry ? "entry" : summary ? "moment" : hasSketch ? "sketch" : "entry";
      preview = snippet(entry || summary, q);
    }

    results.push({
      dateKey,
      date,
      type,
      preview,
      hasSketch,
      isFavourite: data?.isFavourite === true,
    });
  }
  results.sort((a, b) => b.date - a.date);
  return results.slice(0, 25);
}

const typeIcon = { moment: "auto_awesome", entry: "menu_book", sketch: "brush" };

const GlobalSearch = ({ variant = "header", onOpenEntry, onClose, autoFocus = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { entries } = useJournal();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(variant === "overlay");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const locale = i18n.language || undefined;
  const isOverlay = variant === "overlay";

  const results = useMemo(
    () => searchJournal(entries, query, locale),
    [entries, query, locale]
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (autoFocus || isOverlay) {
      const tId = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(tId);
    }
  }, [autoFocus, isOverlay]);

  useEffect(() => {
    if (isOverlay) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOverlay]);

  useEffect(() => {
    if (isOverlay || !open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, isOverlay]);

  const go = (item) => {
    if (!item) return;
    const [y, m, d] = item.dateKey.split("-");
    if (onOpenEntry) {
      onOpenEntry(item.dateKey);
    } else {
      navigate(`/entry/${y}/${Number(m)}/${Number(d)}`);
    }
    setOpen(false);
    setQuery("");
    onClose?.();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[activeIndex]) go(results[activeIndex]);
    } else if (e.key === "Escape") {
      if (isOverlay) onClose?.();
      else {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
  };

  const showPanel = isOverlay || (open && query.trim().length > 0);
  const typeLabel = { moment: t("searchMoment"), entry: t("searchEntry"), sketch: t("searchSketch") };

  const input = (
    <div className={`global-search-field ${query ? "has-query" : ""}`}>
      <span className="material-symbols-outlined global-search-icon">search</span>
      <input
        ref={inputRef}
        type="search"
        className="global-search-input"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={t("searchPlaceholder")}
        aria-label={t("globalSearch")}
        autoComplete="off"
      />
      {query ? (
        <button
          type="button"
          className="global-search-clear"
          onClick={() => setQuery("")}
          aria-label={t("clear")}
        >
          <span className="material-symbols-outlined">close</span>
        </button>
      ) : !isOverlay ? (
        <kbd className="global-search-kbd">⌘K</kbd>
      ) : null}
    </div>
  );

  const list = showPanel && (
    <div className="global-search-results" role="listbox">
      {query.trim() && results.length === 0 ? (
        <div className="global-search-empty">
          <span className="material-symbols-outlined">search_off</span>
          <p>{t("noMatchesFor", { query })}</p>
        </div>
      ) : (
        results.map((item, idx) => (
          <button
            key={item.dateKey}
            type="button"
            role="option"
            aria-selected={idx === activeIndex}
            className={`global-search-item ${idx === activeIndex ? "is-active" : ""}`}
            onMouseEnter={() => setActiveIndex(idx)}
            onClick={() => go(item)}
          >
            <span className="material-symbols-outlined global-search-item-icon">
              {typeIcon[item.type] || "menu_book"}
            </span>
            <span className="global-search-item-body">
              <span className="global-search-item-top">
                <time dateTime={item.dateKey}>{formatShortDate(item.date, locale)}</time>
                <span className="global-search-type">{typeLabel[item.type]}</span>
              </span>
              <span className={`global-search-preview ${item.preview ? "" : "is-empty"}`}>
                {item.preview || "—"}
              </span>
            </span>
            {item.isFavourite && (
              <span className="material-symbols-outlined global-search-star" style={{ fontVariationSettings: "'FILL' 1" }}>
                star
              </span>
            )}
          </button>
        ))
      )}
    </div>
  );

  if (isOverlay) {
    return (
      <div className="global-search-overlay" role="dialog" aria-label={t("globalSearch")}>
        <div className="global-search-overlay-bar">
          <button type="button" className="global-search-overlay-back" onClick={onClose} aria-label={t("back")}>
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          {input}
        </div>
        {list}
      </div>
    );
  }

  return (
    <div className="global-search global-search-header" ref={wrapRef}>
      {input}
      {list}
    </div>
  );
};

export default GlobalSearch;
